import { useDispatch, useSelector } from "react-redux";
import { setArea } from "../context/labels/labelsSlice";
import { selectConfigs, selectLabels } from "../context/selectors";
import useAxiosPrivate from "./useAxiosPrivate";
import { getLabelByArea } from "../services/utils";
import { useMemo } from "react";
import {
  cacheKeyOf,
  createGetLabel,
  isNewer,
  readCachedLabels,
  writeCachedLabels,
} from "@wine2trace/ui/labels/dictionaryLabels";

const useLabels = () => {
    const { area, locale } = useSelector(selectConfigs);
    const labels = useSelector(selectLabels);
    const dispatch = useDispatch();
    const axiosPrivate = useAxiosPrivate();

    // compute ready direttamente dai dati reali
    const ready = useMemo(() => {
      const localeReady = Object.keys(labels?.[locale]?.[area] ?? {}).length > 0;
      const itReady = Object.keys(labels?.it?.[area] ?? {}).length > 0;
      
      return localeReady && itReady;
    }, [labels, locale, area]);

    // The public labels stay in the store after the login: they cover the public pages (the login form
    // included) while the area is already private and its labels are not loaded yet. A key missing
    // everywhere gives the sentinel #<locale>_<area>_<key>, with the token values appended.
    const getLabel = useMemo(
      () =>
        createGetLabel(
          [labels?.[locale]?.[area], labels?.it?.[area], labels?.[locale]?.public, labels?.it?.public],
          (label, tokens) =>
            `#${locale}_${area}_${label}${Object.values(tokens).map((value) => `:${value}`).join("")}`
        ),
      [labels, locale, area]
    );

    const getArea = () => {
        const storedArea = getFromStoreArea(area);
        if (storedArea) {
            return storedArea
        }
        return {}
    }

    const getFromStoreArea = (interestedArea) => {
        return labels?.[interestedArea];
    }

    const checkSession = () => {
      //   return Object.keys(labels?.[locale]?.[area] || {}).length > 0;
      return (
        Object.keys(labels?.[locale]?.[area] || {}).length > 0 &&
        Object.keys(labels?.["it"]?.[area] || {}).length > 0
      );
    };
 
    const isValidDate = (date) => {
        return date instanceof Date && !isNaN(date.getTime());
    }

    // In the user's language; a failure is thrown to setLabels, which keeps the cache. With since (the
    // date of the cache): both not modified gives null (keep the cache), one changed reads both again
    // without it, so the merged labels are whole.
    const getLabelFromServer = async (since) => {
      const langLabels = await getLabelByArea(area, axiosPrivate, undefined, locale, since);
      const errorLabels = await getLabelByArea("error", axiosPrivate, undefined, locale, since);
      if (since) {
        const notModified = [langLabels, errorLabels].filter((answer) => answer?.status === 304).length;
        if (notModified === 2) return null;
        if (notModified === 1) return getLabelFromServer();
      }
      const response = langLabels?.data || {};
      const errorResponse = errorLabels?.data || {};

      // ---- flatten error labels (come già fai)
      const errors = {};
      for (const [key, dict] of Object.entries(errorResponse)) {
        for (const [id, value] of Object.entries(dict)) {
          errors[`${key}.${id}`] = value;
        }
      }

      // ---- timestamps
      let lastModified = null;
      let lastModifiedError = null;

      if (langLabels?.headers?.["last-modified"]) {
        lastModified = new Date(langLabels.headers["last-modified"]);
      }
      if (errorLabels?.headers?.["last-modified"]) {
        lastModifiedError = new Date(errorLabels.headers["last-modified"]);
      }

      const dates = [lastModified, lastModifiedError].filter(isValidDate);
      const maxDate = dates.length
        ? dates.reduce((a, b) => (a > b ? a : b))
        : null;

      // ---- 🔑 MERGE CON MASTER IT
      let mergedResponse = { ...response, ...errors };

      if (locale !== "it") {
        const itCache = readCachedLabels(localStorage, cacheKeyOf(area, "it"));
        if (itCache) {
          const itLabels = itCache.labels;

          // fallback: IT base + lingua corrente override
          mergedResponse = {
            ...itLabels,
            ...mergedResponse,
          };
        }
      }

      return {
        ...mergedResponse,
        ...(maxDate && { lastModifiedTimestamp: maxDate.toISOString() }),
      };
    };

    // const getLabelFromServer = async () => {
    //     const langLabels = await getLabelByArea(area, axiosPrivate);
    //     const response = langLabels?.data; 
    //     const errorLabels = await getLabelByArea('error', axiosPrivate);
    //     const errorResponse = errorLabels?.data; 

    //     const errors = {};
    //     if (errorResponse) {
    //         for (const [key, dict] of Object.entries(errorResponse)) {
    //             for (const [id, value] of Object.entries(dict)) {
    //                 errors[`${key}.${id}`] = value;
    //             }
    //         }
    //     }
        
    //     let lastModified = null;
    //     let lastModifiedError = null;
        
    //     if (langLabels.headers && langLabels.headers['last-modified'])
    //         lastModified = new Date(langLabels.headers?.["last-modified"]);
    //     if (errorLabels.headers && errorLabels.headers['last-modified'])
    //         lastModifiedError = new Date(errorLabels.headers?.["last-modified"]);

    //     const dates = [lastModified, lastModifiedError].filter(isValidDate);
    //     const maxDate = dates.reduce((a, b) => a > b ? a : b, undefined);
                
    //     return { ...response, ...errors, ...(maxDate && { lastModifiedTimestamp: maxDate.toISOString() }) } || {};
    // }

    const setLabels = async () => {
        if (checkSession()) {
          return;
        }

        const key = cacheKeyOf(area, locale);
        const cached = readCachedLabels(localStorage, key);

        if (cached) {
          // The cached labels go to the store right away; the server answer replaces them only when newer.
          dispatch(setArea({ locale: locale, area: area, payload: cached.labels }));

          try {
            const serverLabels = await getLabelFromServer(cached.lastModified ?? undefined);
            if (serverLabels?.lastModifiedTimestamp && isNewer(serverLabels.lastModifiedTimestamp, cached)) {
              writeCachedLabels(localStorage, key, { labels: serverLabels, lastModified: serverLabels.lastModifiedTimestamp });
              dispatch(setArea({ locale: locale, area: area, payload: serverLabels }));
            }
          } catch (error) {
            console.error(error);
          }
        } else {
          try {
            const serverLabels = await getLabelFromServer();
            const lastModified = serverLabels.lastModifiedTimestamp ? serverLabels.lastModifiedTimestamp : new Date().toISOString();
            writeCachedLabels(localStorage, key, { labels: serverLabels, lastModified });
            dispatch(setArea({ locale: locale, area: area, payload: serverLabels }));
          } catch (error) {
            console.error(error);
            // handle the case where there are no labels in localStorage
          }
        }
      };

    return [getLabel, getArea, setLabels, ready]

}


export default useLabels